import React, { useEffect, useState } from "react"
import { Row, Col, Badge, Container, Button, Spinner, Stack, Modal, Pagination } from "react-bootstrap"
import { useCompanies } from "../../api/companies/store"
import { useAuth } from "../../api/auth/store"
import { listDeviceRequests, deviceRequestAction, listAllowedDevices } from "../../api/companies/actions"
import { DeviceApprovalRequest } from "../../api/companies/data"
import Loading from "../../components/Loading"
import CStack from "../../components/CStack"
import CAccordion from "../../components/CAccordion"
import ButtonControl from "../../components/ButtonControl"
import useUXStore from "../../api/ux/store"
import { ADMIN_ROLE, OWNER_ROLE, SUPPORT_ROLE } from "../../constants"
import SwitchControl from "../../components/SwitchControl"
import BaseControl from "../../components/BaseControl"

const DeviceRequestItem = ({ req, canEdit, onAction, hideUser }: {
    req: DeviceApprovalRequest,
    canEdit: boolean,
    onAction: (deviceId: string, action: "approve" | "reject") => void,
    hideUser?: boolean
}) => {
    return (
        <CAccordion
            label={hideUser ? (req.device_name ?? req.device_type) : `${req.user_display} - ${req.device_name}`}
        >
            <Container fluid className="py-3">
                <Row>
                    <Col md={6}>
                        <div className="mb-2"><strong>User:</strong> {req.user_display} ({req.user_email})</div>
                        <div className="mb-2"><strong>Device:</strong> {req.device_name} ({req.device_type})</div>
                        <div className="mb-2"><strong>Requested:</strong> {new Date(req.requested_at).toLocaleString()}</div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-2"><strong>IP Address:</strong> {req.ip || "Unknown"}</div>
                        <div className="mb-2"><strong>Location:</strong> {req.location || "Unknown"}</div>
                        {req.action_by && <div className="mb-2"><strong>Action By:</strong> {req.action_by}</div>}
                        {req.action_at && <div className="mb-2"><strong>Action At:</strong> {new Date(req.action_at).toLocaleString()}</div>}
                    </Col>
                </Row>
                {canEdit && (
                    <>
                        {
                            req.status === "pending" ? (
                                <>
                                    <BaseControl text="Approve Device">
                                        <Stack direction="horizontal" gap={2}>
                                            <Button size="sm" onClick={() => onAction(req.device_id, "reject")} variant="outline-danger">
                                                Reject
                                            </Button>
                                            <Button size="sm" onClick={() => onAction(req.device_id, "approve")} variant="primary">Approve</Button>
                                        </Stack>
                                    </BaseControl>
                                </>
                            ) : (
                                <ButtonControl
                                    text="Revoke Access"
                                    variant="outline-danger"
                                    buttonLabel="Revoke"
                                    onClicked={() => onAction(req.device_id, "reject")}
                                />
                            )
                        }
                    </>
                )}
            </Container>
        </CAccordion>
    )
}

const UserDeviceGroup = ({ userLabel, devices, canEdit, onAction }: {
    userLabel: string,
    devices: DeviceApprovalRequest[],
    canEdit: boolean,
    onAction: (deviceId: string, label: string) => void
}) => {
    return (
        <div className="device-group-container mb-2">
            <CAccordion label={userLabel}>
                <div style={{ background: 'transparent', border: 'none' }} >
                    <CStack padding="10px 0px 20px 40px">
                        {devices.map(dev => (
                            <DeviceRequestItem
                                key={dev.device_id + "_grouped"}
                                req={dev}
                                canEdit={canEdit}
                                hideUser
                                onAction={(id) => onAction(id, dev.device_name)}
                            />
                        ))}
                    </CStack>
                </div>
            </CAccordion>
        </div>
    )
}

const CompanyDeviceApproval = () => {
    const { profile, hasRole, retrieveCompanyProfile, sessionDetails, loadSession } = useAuth(state => state)
    const { toggleDeviceApproval } = useCompanies(state => state)
    const { addToast } = useUXStore(state => state)

    const [loading, setLoading] = useState(false)
    const [approvalEnabled, setApprovalEnabled] = useState(profile?.device_approval || false)
    const [pendingRequests, setPendingRequests] = useState<DeviceApprovalRequest[]>([])
    const [allowedDevices, setAllowedDevices] = useState<DeviceApprovalRequest[]>([])
    const [loadingRequests, setLoadingRequests] = useState(false)
    const [confirmDevice, setConfirmDevice] = useState<{ id: string, label: string } | null>(null)

    // Pagination state
    const [pendingPage, setPendingPage] = useState(1)
    const [allowedPage, setAllowedPage] = useState(1)
    const [totalPending, setTotalPending] = useState(0)
    const [totalAllowed, setTotalAllowed] = useState(0)
    const pageSize = 30

    useEffect(() => {
        if (!sessionDetails) {
            loadSession()
        }
    }, [sessionDetails])

    useEffect(() => {
        if (!profile) {
            setApprovalEnabled(false)
            retrieveCompanyProfile()
        } else {
            setApprovalEnabled(profile.device_approval || false)
        }
    }, [profile])

    useEffect(() => {
        fetchPending(pendingPage)
    }, [pendingPage])

    useEffect(() => {
        fetchAllowed(allowedPage)
    }, [allowedPage])

    const fetchPending = async (page: number) => {
        setLoadingRequests(true)
        try {
            const offset = (page - 1) * pageSize
            const pendingRes = await listDeviceRequests("pending", offset)
            if (pendingRes.Code === "200") {
                setPendingRequests(pendingRes.Msg.requests || [])
                setTotalPending(pendingRes.Msg.total || 0)
            }
        } catch (err) {
            console.error(err)
        } finally {
            setLoadingRequests(false)
        }
    }

    const fetchAllowed = async (page: number) => {
        setLoadingRequests(true)
        try {
            const offset = (page - 1) * pageSize
            const allowedRes = await listAllowedDevices(offset)
            if (allowedRes.Code === "200") {
                setAllowedDevices(allowedRes.Msg.devices || [])
                setTotalAllowed(allowedRes.Msg.total || 0)
            }
        } catch (err) {
            console.error(err)
        } finally {
            setLoadingRequests(false)
        }
    }

    const fetchData = () => {
        fetchPending(pendingPage)
        fetchAllowed(allowedPage)
    }

    const handleToggle = async () => {
        const nextState = !approvalEnabled
        setLoading(true)
        try {
            const code = await toggleDeviceApproval(nextState)
            if (code === "200") {
                retrieveCompanyProfile()
                setApprovalEnabled(nextState)
                addToast("Success", `Device approval ${nextState ? "enabled" : "disabled"}`)
            } else {
                addToast("Error", "Failed to update device approval setting", null, "danger")
            }
        } catch (err) {
            addToast("Error", "An error occurred", null, "danger")
        } finally {
            setLoading(false)
        }
    }

    const handleAction = async (deviceId: string, action: "approve" | "reject") => {
        try {
            const code = await deviceRequestAction(deviceId, action)
            if (code === "200") {
                addToast("Success", `Device ${action === "approve" ? "approved" : "rejected"} successfully`)
                fetchData() // Refresh lists
            } else {
                addToast("Error", `Failed to ${action} device`, null, "danger")
            }
        } catch (err) {
            addToast("Error", "An error occurred", null, "danger")
        }
    }

    const generatePageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }
        const pages: (number | string)[] = []
        pages.push(1)
        if (currentPage > 3) {
            pages.push('...')
        } else if (currentPage > 1) {
            pages.push(2)
        }
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (!pages.includes(i)) pages.push(i)
        }
        if (currentPage < totalPages - 2) {
            pages.push('...')
        } else if (currentPage < totalPages && totalPages > 2) {
            pages.push(totalPages - 1)
        }
        if (totalPages > 1) pages.push(totalPages)
        return pages
    }

    const renderPagination = (currentPage: number, total: number, onPageChange: (p: number) => void) => {
        const totalPages = Math.ceil(total / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="d-flex justify-content-between align-items-center mt-3 px-3">
                <div className="small text-muted">
                    Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, total)} of {total}
                </div>
                <Pagination size="sm" className="mb-0">
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
                    {generatePageNumbers(currentPage, totalPages).map((p, i) => (
                        p === '...' ? <Pagination.Ellipsis key={`el-${i}`} disabled /> :
                            <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p as number)}>
                                {p}
                            </Pagination.Item>
                    ))}
                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)} />
                </Pagination>
            </div>
        )
    }

    if (!sessionDetails) {
        return <Loading center />
    }

    const canEdit = (hasRole(ADMIN_ROLE) || hasRole(OWNER_ROLE))

    return (
        <>
            <CStack header="">
                <SwitchControl
                    text="Device Approval"
                    caption="Require administrator approval for new devices."
                    value={approvalEnabled}
                    onChanged={handleToggle}
                    edit={!loading && canEdit}
                />
                {!approvalEnabled && <div className="mt-2 p-3 bg-light border-start border-primary border-4 rounded-end small text-secondary">
                    <Stack direction="horizontal" gap={2} className="align-items-start">
                        <div className="text-primary mt-1">
                            <i className="bi bi-shield-check"></i>
                        </div>
                        <div>
                            <strong>Enhanced Protection:</strong> Enabling this adds a critical layer of security. Even if user credentials are leaked, unauthorized access is blocked until an administrator verifies the new device. This helps prevent data breaches from stolen accounts.
                        </div>
                    </Stack>
                </div>}
            </CStack>

            {totalPending > 0 && (
                <CStack header={`Pending Requests (${totalPending})`}>
                    {loadingRequests ? <Loading /> : (
                        <>
                            {pendingRequests.map(req => (
                                <DeviceRequestItem
                                    key={req.device_id + "_pending"}
                                    req={req}
                                    canEdit={canEdit}
                                    onAction={handleAction}
                                />
                            ))}
                            {renderPagination(pendingPage, totalPending, setPendingPage)}
                        </>
                    )}
                </CStack>
            )}

            <CStack header={`Allowed Devices (${totalAllowed})`}>
                {loadingRequests ? <Loading /> : (
                    allowedDevices.length === 0 ? (
                        <div className="p-4 text-center text-muted border rounded bg-light">No approved devices</div>
                    ) : (
                        <>
                            {Object.entries(allowedDevices.reduce((acc, dev) => {
                                if (!acc[dev.user_id]) acc[dev.user_id] = { label: dev.user_display, devices: [] }
                                acc[dev.user_id].devices.push(dev)
                                return acc
                            }, {} as Record<string, { label: string, devices: DeviceApprovalRequest[] }>) || {})
                                .sort(([, a], [, b]) => a.label.localeCompare(b.label))
                                .map(([userId, group]) => (
                                    <UserDeviceGroup
                                        key={userId}
                                        userLabel={group.label}
                                        devices={group.devices}
                                        canEdit={canEdit}
                                        onAction={(id, label) => setConfirmDevice({ id, label })}
                                    />
                                ))}
                            {renderPagination(allowedPage, totalAllowed, setAllowedPage)}
                        </>
                    )
                )}
            </CStack>

            {confirmDevice && (
                <Modal show onHide={() => setConfirmDevice(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Revoke Device Access</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure you want to revoke access for <strong>{confirmDevice.label}</strong>?
                        The user will need to request approval again before they can log in from this device.
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setConfirmDevice(null)}>Cancel</Button>
                        <Button variant="danger" onClick={() => {
                            handleAction(confirmDevice.id, "reject")
                            setConfirmDevice(null)
                        }}>Revoke</Button>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    )
}

export default CompanyDeviceApproval
